import { pageNames } from 'src/routes/pathParams'
import { IS_BROWSER } from './application'
import { enablePORedesignFeature } from './commonUtils'
import { getPoSponsorId, getPersonalOfferPitchId } from './mySiteUtils'

/**
 * Builds the path-based order confirmation URL for the current PO context.
 *
 * Purpose: payment success handlers (PayPal, Klarna, etc.) historically build
 * the redirect URL by splitting `window.location.href` on a known checkout
 * segment (e.g. `/expressCheckout`) and concatenating `/orderConfirmation`.
 * BuyNow flows initiated directly from a PO landing page (URL like
 * `/<locale>/personal-offer/<sponsor>?pitchId=<id>`) do not contain any of
 * those segments, so the split returns the full URL — including the query
 * string — as `arr[0]`, producing a malformed redirect like
 * `/personal-offer/<sponsor>?pitchId=<id>/orderConfirmation`.
 *
 * This helper produces the correct path-only URL
 * `${origin}/<locale>/personal-offer/<sponsor>/orderConfirmation` (with
 * `?isNativeApp=true` when requested) and returns null when the current URL
 * is NOT a PO route, so non-PO checkout flows are not affected.
 *
 * Scope: gated strictly on `window.location.pathname.includes('/personal-offer/')`
 * so this never fires on regular storefront / mysite redirects.
 *
 * @param {Object} [options]
 * @param {boolean} [options.isNativeApp=false] - When true, appends `?isNativeApp=true`.
 * @returns {string|null} Absolute redirect URL or null when not in PO context.
 */
const getPOOrderConfirmationUrl = ({ isNativeApp = false } = {}) => {
  if (!IS_BROWSER) return null
  const currentPath = window?.location?.pathname || ''
  if (!currentPath.includes('/personal-offer/')) return null
  const sponsorId = getPoSponsorId()
  if (!sponsorId) return null
  const basePath = currentPath.split('/personal-offer/')[0]
  const query = isNativeApp ? '?isNativeApp=true' : ''
  return `${window.location.origin}${basePath}/personal-offer/${sponsorId}${pageNames.orderConfirmation}${query}`
}

/**
 * PO V2 only: returns the canonical PO landing path
 * `/<locale>/personal-offer/<sponsor>[?pitchId=<id>]` (relative, no trailing
 * slash) for SPA navigation via `history.pushState`. Returns `null` off PO
 * or when the V2 flag is off, so callers can fall back safely.
 */
const getPOLandingUrl = () => {
  if (!IS_BROWSER) return null
  if (!enablePORedesignFeature()) return null
  const currentPath = window?.location?.pathname || ''
  if (!currentPath.includes('/personal-offer/')) return null
  const sponsorId = getPoSponsorId()
  if (!sponsorId) return null
  const pitchId = getPersonalOfferPitchId()
  const basePath = currentPath.split('/personal-offer/')[0]
  return `${basePath}/personal-offer/${sponsorId}${
    pitchId ? `?pitchId=${pitchId}` : ''
  }`
}

/**
 * PO-only common redirect helper. When the current route is a PO route
 * (URL contains `/personal-offer/`) AND the PO redesign feature flag is
 * enabled, navigates the browser to the canonical PO orderConfirmation
 * URL and returns `true` so callers can early-return. In every other
 * context (SSR, feature flag off, non-PO route, missing sponsor) it is
 * a no-op and returns `false`, so existing storefront / mysite /
 * checkout flows remain unchanged.
 *
 * Use this from any payment success handler that currently does:
 *
 *   const url = getPOOrderConfirmationUrl({ isNativeApp })
 *   if (url) { window.location.href = url; return }
 *
 * @param {Object} [options]
 * @param {boolean} [options.isNativeApp=false] - Appends `?isNativeApp=true`.
 * @param {'assign'|'replace'} [options.mode='assign'] - Use
 *   `window.location.replace` when `'replace'`, else `window.location.href`.
 * @returns {boolean} `true` when a PO redirect was issued (caller should
 *   bail out), `false` when no redirect happened (caller continues normal flow).
 */
const redirectToPOOrderConfirmation = ({
  isNativeApp = false,
  mode = 'assign',
} = {}) => {
  if (!IS_BROWSER) return false
  if (!enablePORedesignFeature()) return false
  const url = getPOOrderConfirmationUrl({ isNativeApp })
  if (!url) return false
  if (mode === 'replace') {
    window?.location?.replace(url)
  } else {
    window.location.href = url
  }
  return true
}

export {
  redirectToPOOrderConfirmation,
  getPOLandingUrl,
  getPOOrderConfirmationUrl,
}
